import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import _get from 'lodash/get';
import { TaskCompletion } from 'graphql-common';
import useEligibleCompletionsForIntervention from '@lib/components/TaskCompletions/utils/useEligibleCompletionsForIntervention';
import { logger } from '@lib/services/logger';

type Args = {
  groupKey?: string | null;
  // Already-loaded completions — used to resolve the id-only stubs
  // "select all" puts in `selectedItems`.
  collection: TaskCompletion[];
  selectedItems: TaskCompletion[];
  getInterventionUrl: (interventionId: string) => string;
  // Host-provided; the create-intervention actions are undefined without it.
  onCreateInterventionForSelected?: (completions: TaskCompletion[]) => void;
  // Runs before navigating to an intervention (e.g. close a modal whose host
  // stays mounted).
  onBeforeNavigate?: () => void;
  initialBulkCompareIds?: string[] | null;
};

// Row / bulk actions shared by every completions list: compare, view the
// linked intervention and create an intervention from eligible completions.
export default function useTaskCompletionsSelectionActions({
  groupKey,
  collection,
  selectedItems,
  getInterventionUrl,
  onCreateInterventionForSelected,
  onBeforeNavigate,
  initialBulkCompareIds,
}: Args) {
  const navigate = useNavigate();
  const [bulkCompareIds, setBulkCompareIds] = useState<string[] | null>(
    initialBulkCompareIds ?? null,
  );

  const getEligibleCompletions =
    useEligibleCompletionsForIntervention(groupKey);

  const onCompareSelected = () => {
    const ids = selectedItems.map((item) => String(item.id)).filter(Boolean);
    if (!ids.length) return;
    logger.info('TaskCompletions: compare started', {
      groupKey,
      count: ids.length,
    });
    setBulkCompareIds(ids);
  };

  const onViewIntervention = useCallback(
    (item?: TaskCompletion) => {
      const interventionId = _get(item, 'task.intervention.id');
      if (!interventionId) return;
      onBeforeNavigate?.();
      navigate(getInterventionUrl(interventionId));
    },
    [navigate, getInterventionUrl, onBeforeNavigate],
  );

  // The row action is only offered for eligible completions
  // (`canInterventionCreate`), but the completion may have been covered since
  // the list loaded — stop there rather than open an unsubmittable form.
  const onCreateIntervention = useMemo(() => {
    if (!onCreateInterventionForSelected) return undefined;
    return async (item?: TaskCompletion) => {
      if (!item) return;
      const eligible = await getEligibleCompletions([item]);
      if (!eligible?.length) return;
      logger.info('TaskCompletions: create intervention', {
        groupKey,
        count: eligible.length,
      });
      onCreateInterventionForSelected(eligible);
    };
  }, [onCreateInterventionForSelected, getEligibleCompletions, groupKey]);

  const onCreateInterventionBulk = useMemo(() => {
    if (!onCreateInterventionForSelected) return undefined;
    return async () => {
      if (!selectedItems.length) return;
      // "Select all" can materialize `selectedItems` as id-only stubs (see
      // useTableItemsBulkSelectWithAllIds) — look the full completion up in
      // the already-loaded `collection` to get its date label.
      const completionById = new Map(
        collection.map((completion) => [completion.id, completion]),
      );
      const eligible = await getEligibleCompletions(
        selectedItems.map((item) => completionById.get(item.id) || item),
      );
      // Nothing eligible (the toast already says why): opening the form with
      // the Task but no completions would leave it unsubmittable.
      if (!eligible?.length) return;
      logger.info('TaskCompletions: create intervention', {
        groupKey,
        count: eligible.length,
      });
      onCreateInterventionForSelected(eligible);
    };
  }, [
    onCreateInterventionForSelected,
    selectedItems,
    collection,
    getEligibleCompletions,
    groupKey,
  ]);

  return {
    bulkCompareIds,
    setBulkCompareIds,
    onCompareSelected,
    onViewIntervention,
    onCreateIntervention,
    onCreateInterventionBulk,
  };
}
